import React from "react";
import clsx from "clsx";
import { FiClock, FiShield, FiUser, FiTrendingUp, FiX } from "react-icons/fi";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import Logo from "Assets/images/logo.svg";
import BackgroundImage from "Assets/images/assessment-bg.webp";
import styles from "./AssessmentWelcomeUplift.module.scss";

export interface AssessmentWelcomeUpliftProps {
  onBegin: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

const AssessmentWelcomeUplift: React.FC<AssessmentWelcomeUpliftProps> = ({
  onBegin,
  onClose,
  isLoading = false,
}) => {
  const { t } = useTranslation(["assessment"]);

  const BULLETS = [
    { icon: <FiClock size={16} />, text: t("assessment:welcome.bulletDuration") },
    {
      icon: <FiShield size={16} />,
      text: t("assessment:welcome.bulletPrivacy"),
    },
    { icon: <FiUser size={16} />, text: t("assessment:welcome.bulletInsights") },
    {
      icon: <FiTrendingUp size={16} />,
      text: t("assessment:welcome.bulletRetake"),
    },
  ];

  return (
    <div
      className={styles.container}
      data-testid="assessment-welcome-uplift"
      style={{ backgroundImage: `url(${BackgroundImage})` }}
    >
      <div className={styles.header}>
        <img src={Logo} alt="Wone Logo" className={styles.logo} />
        <button
          type="button"
          className={styles["close-button"]}
          aria-label={t("assessment:welcome.closeLabel")}
          onClick={onClose}
        >
          <FiX size={20} />
        </button>
      </div>

      {isLoading ? (
        <div
          className={styles["loading-body"]}
          data-testid="assessment-welcome-loading"
        >
          <div className={clsx("spinner-border", styles["loading-spinner"])} />
        </div>
      ) : (
        <>
          <div className={styles.body}>
            <div className={styles.content}>
              <h1 className={styles.heading}>
                {t("assessment:welcome.title")}
              </h1>

              <div className={styles.paragraphs}>
                <p className={styles.paragraph}>
                  {t("assessment:welcome.body1")}
                </p>
                <p className={styles.paragraph}>
                  {t("assessment:welcome.body2")}
                </p>
              </div>

              <div className={styles.bullets}>
                {BULLETS.map(({ icon, text }) => (
                  <div className={styles.bullet} key={text}>
                    {icon}
                    <p className={styles["bullet-text"]}>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              variant="enclosed-primary"
              monochromeColour="white"
              className={styles["begin-button"]}
              onClick={onBegin}
            >
              {t("assessment:welcome.begin")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentWelcomeUplift;
